import React from "react";
import { Formik, Form, Field, FieldProps } from "formik";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { useToast } from "hooks/useToast";
import { YYYY_MM_DD } from "constants/date";
import {
  useEditEntityDataAttributesMutation,
  useEditEntityStateDataMutation,
} from "store/apis/entity";
import { EntityDataAttribute } from "types/Models/entity";
import { BackendError } from "types/utils/error";
import dayjs from "dayjs";
import BareInput from "components/DesignSystem/Input/BareInput";
import { isSosNumberAttr } from "./constants";

export const MISSING_FIELD_LABELS: Record<string, string> = {
  date_of_incorporation: "Date of incorporation",
  tax_year: "Tax year end",
  tax_type: "LLC taxed as",
  foreign_qualification_date: "Foreign qualification date",
  sos_number: "SOS Number",
};

type MissingInfoModalProps = {
  isOpen: boolean;
  close: () => void;
  missingFields: EntityDataAttribute[];
  entityAttributes: EntityDataAttribute[];
  groupId: string;
  entityId: string;
  stateId: string;
  // Called when user clicks "Let Inkle register" from the SOS number field prompt
  onLetInkleHandle?: () => void;
};

export const MissingInfoModal = ({
  isOpen,
  close,
  missingFields,
  entityAttributes,
  groupId,
  entityId,
  stateId,
  onLetInkleHandle,
}: MissingInfoModalProps) => {
  const { successToast, alertToast } = useToast();
  const [editEntityDataAttributes, { isLoading: isSavingEntity }] =
    useEditEntityDataAttributesMutation();
  const [editEntityStateData, { isLoading: isSavingState }] =
    useEditEntityStateDataMutation();

  const isSaving = isSavingEntity || isSavingState;

  const initialValues = Object.fromEntries(
    missingFields.map((f) => [f.name, ""])
  );

  const handleSubmit = async (formValues: Record<string, string>) => {
    const entityPayload: Record<string, string> = {};
    const statePayload: Record<string, string> = {};

    for (const [name, value] of Object.entries(formValues)) {
      if (!value) continue;
      const field = missingFields.find((f) => f.name === name) as
        | (EntityDataAttribute & { isStateField?: boolean })
        | undefined;
      if (field?.isStateField) {
        statePayload[name] = value;
      } else {
        entityPayload[name] = value;
      }
    }

    try {
      if (Object.keys(entityPayload).length > 0) {
        await editEntityDataAttributes({
          groupId,
          entityId,
          payload: entityPayload,
        }).unwrap();
      }
      if (Object.keys(statePayload).length > 0) {
        await editEntityStateData({
          groupId,
          entityId,
          stateId,
          payload: statePayload,
        }).unwrap();
      }
      successToast({ message: "Information updated successfully" });
      close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const getFullAttr = (name: string) =>
    entityAttributes.find((a) => a.name === name);

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content useCustomOverlay>
        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          {({ values, setFieldValue, submitForm }) => (
            <Form>
              <Modal.Header>
                <Modal.Title>Add missing information</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body className="t-flex t-flex-col t-gap-4">
                {missingFields.map((field) => {
                  const fullAttr = getFullAttr(field.name);
                  const type = fullAttr?.type ?? "INPUT";
                  const labelKey =
                    Object.keys(MISSING_FIELD_LABELS).find((key) =>
                      field.name.startsWith(key)
                    ) ?? field.name;
                  const label =
                    MISSING_FIELD_LABELS[labelKey] ??
                    fullAttr?.label ??
                    field.label;

                  const isSosField = isSosNumberAttr({
                    field_key: field.name,
                    label: field.label ?? "",
                  });

                  if (type === "SELECT" && fullAttr?.options?.length) {
                    const selectedOption =
                      fullAttr.options.find(
                        (o) => o.value === values[field.name]
                      ) ?? null;
                    return (
                      <Combobox
                        key={field.name}
                        label={label}
                        value={selectedOption}
                        onChange={(option) =>
                          void setFieldValue(
                            field.name,
                            (option as { value: string } | null)?.value ?? ""
                          )
                        }
                        options={fullAttr.options.map((o) => ({
                          label: o.label,
                          value: o.value,
                        }))}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    );
                  }

                  if (isSosField) {
                    return (
                      <div
                        key={field.name}
                        className="t-flex t-flex-col t-gap-2"
                      >
                        <Field name={field.name}>
                          {({ field: fieldProps }: FieldProps) => (
                            <BareInput
                              {...fieldProps}
                              label={label}
                              placeholder="Enter SOS Number"
                            />
                          )}
                        </Field>
                        {onLetInkleHandle && (
                          <Banner
                            type="primary"
                            description="Not registered yet? Let Inkle handle the foreign qualification for you."
                            icon={<span />}
                            cta={
                              <Button
                                type="button"
                                customType="primary"
                                size="small"
                                onClick={() => {
                                  close();
                                  onLetInkleHandle();
                                }}
                              >
                                Register with Inkle
                              </Button>
                            }
                          />
                        )}
                      </div>
                    );
                  }

                  return (
                    <Field key={field.name} name={field.name}>
                      {({ field: fieldProps }: FieldProps) => (
                        <DateInput
                          {...fieldProps}
                          label={label}
                          portalId={field.name}
                          overriddenOnChange={(date) =>
                            void setFieldValue(
                              field.name,
                              dayjs(date).format(YYYY_MM_DD)
                            )
                          }
                        />
                      )}
                    </Field>
                  );
                })}
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Button onClick={close} customType="secondary" type="button">
                  Cancel
                </Button>
                <Button
                  onClick={() => void submitForm()}
                  customType="primary"
                  isLoading={isSaving}
                  disabled={isSaving}
                  type="button"
                >
                  Save
                </Button>
              </Modal.FooterButtonGroup>
            </Form>
          )}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};
